import {useRef, lazy, Suspense} from 'react'

import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'

import {CTABanner, Section} from '@primer/react-brand'

import type {GenericSectionWithIds, GenericContent} from '../../../../brand/lib/types/contentful'

import {useIntersectionObserver} from '@github-ui/swp-core/lib/hooks/use-intersection-observer'
import {useDomReady} from '../../../../lib/utils/platform'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Spacer} from '../../_shared'

import {CtaGroup} from '../../_components/CtaGroup/CtaGroup'

import CtaUniverse from '../../CtaUniverse'

const CtaWebGL = lazy(() => import('../../CtaWebGL'))

type Props = {
  contentfulContent: GenericSectionWithIds
}

export function CtaSection(props: Props) {
  const isUniverseAnimationsEnabled = isFeatureEnabled('site_homepage_universe_animations')
  const {contentfulContent} = props

  const {ctaSectionContent} = contentfulContent.ids as {
    ctaSectionContent: GenericContent
  }

  const {heading, subheading, ctas} = ctaSectionContent?.fields

  const ctaRef = useRef<HTMLDivElement | null>(null)
  const {isIntersecting: isCtaInView} = useIntersectionObserver(ctaRef, {threshold: 0.4, isOnce: true})
  const isDomReady = useDomReady()

  return (
    <Section id="cta">
      <Spacer size="100px" />

      <div className="lp-Cta-mascots-wrap">
        {!isUniverseAnimationsEnabled && <svg width="2280" height="1200" aria-hidden="true" />}
        {isDomReady && (
          <Suspense fallback={null}>{isUniverseAnimationsEnabled ? <CtaUniverse /> : <CtaWebGL />}</Suspense>
        )}
      </div>

      <Spacer size="48px" size1012="80px" />

      <CTABanner
        ref={ctaRef}
        className={`lp-Cta ${isCtaInView ? 'is-visible' : ''}`}
        align="center"
        hasBackground={false}
        hasShadow={false}
      >
        {ctaSectionContent && heading ? <CTABanner.Heading as="h2">{heading}</CTABanner.Heading> : null}

        {ctaSectionContent && subheading ? (
          <CTABanner.Description className="lp-Cta-description">
            {documentToPlainTextString(subheading)}
          </CTABanner.Description>
        ) : null}

        {ctas ? <CtaGroup contentfulContent={ctas} location="bottom_cta_section" /> : null}
      </CTABanner>
    </Section>
  )
}

try{ CtaWebGL.displayName ||= 'CtaWebGL' } catch {}
try{ CtaSection.displayName ||= 'CtaSection' } catch {}